import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { selectCurrentGroupId } from './groups_slice';
import { parseTimespanFilter, parseAgeFilter } from '../../_helpers/misc_functions';
import { fetchData } from './api';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AsyncDataSlot {
  data: unknown;
  status: AsyncStatus;
  error: string | null;
}

interface MicrolearningsFilters {
  timespan: string;
  start_age: string | null;
  end_age: string | null;
  gender: string;
  department: string;
  role: string;
  specialty: string;
  years_in_profession: string;
}

interface MicrolearningsState {
  rollingMetrics: {
    totalHoursWatched: AsyncDataSlot;
    totalMicrolearningsWatched: AsyncDataSlot;
    totalCreditsClaimed: AsyncDataSlot;
    mostPopular: AsyncDataSlot;
  };
  filters: MicrolearningsFilters;
  mostWatched: AsyncDataSlot;
  mostClaimed: AsyncDataSlot;
}

const initialState: MicrolearningsState = {
  rollingMetrics: {
    totalHoursWatched: { data: 0, status: 'idle', error: null },
    totalMicrolearningsWatched: { data: 0, status: 'idle', error: null },
    totalCreditsClaimed: { data: 0, status: 'idle', error: null },
    mostPopular: { data: null, status: 'idle', error: null },
  },
  filters: {
    timespan: '90 Days',
    start_age: null,
    end_age: null,
    gender: 'All',
    department: 'All',
    role: 'All',
    specialty: 'All',
    years_in_profession: 'All',
  },
  mostWatched: { data: null, status: 'idle', error: null },
  mostClaimed: { data: null, status: 'idle', error: null },
};

const makeRollingThunk = (name: string, endpoint: string, statusKey: keyof MicrolearningsState['rollingMetrics']) =>
  createAsyncThunk<unknown, void, { state: RootState }>(
    `microlearnings/${name}`,
    async (_, { getState, rejectWithValue }) => {
      const state = getState();
      const { start_date, end_date } = parseTimespanFilter({ timespan: '90 Days' });
      const filters = { practice_group_id: selectCurrentGroupId(state), start_date, end_date };
      try {
        return await fetchData({ endpoint, filters });
      } catch (error: any) {
        return rejectWithValue(error.message || `Failed to fetch ${name}`);
      }
    },
    {
      condition: (_, { getState }) =>
        getState().microlearnings.rollingMetrics[statusKey].status !== 'loading',
    }
  );

export const fetchHoursWatched = makeRollingThunk('fetchHoursWatched', 'insights/microlearnings/hours_watched', 'totalHoursWatched');
export const fetchTotalWatched = makeRollingThunk('fetchTotalWatched', 'insights/microlearnings/total_watched', 'totalMicrolearningsWatched');
export const fetchCreditsClaimed = makeRollingThunk('fetchCreditsClaimed', 'insights/microlearnings/total_credits_claimed', 'totalCreditsClaimed');
export const fetchPopularSeries = makeRollingThunk('fetchPopularSeries', 'insights/microlearnings/most_watched_series', 'mostPopular');

export const fetchMostWatched = createAsyncThunk<unknown, void, { state: RootState }>(
  'microlearnings/fetchMostWatched',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.microlearnings.filters;
    const { start_date, end_date } = parseTimespanFilter(filters as any);
    const { start_age, end_age } = parseAgeFilter(filters.start_age as any);
    const allFilters = { practice_group_id: selectCurrentGroupId(state), ...filters, start_date, end_date, start_age, end_age };
    try {
      return await fetchData({ endpoint: 'insights/microlearnings/most_watched_microlearnings', filters: allFilters as any });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch most watched microlearnings');
    }
  },
  { condition: (_, { getState }) => getState().microlearnings.mostWatched.status !== 'loading' }
);

export const fetchMostClaimed = createAsyncThunk<unknown, void, { state: RootState }>(
  'microlearnings/fetchMostClaimed',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.microlearnings.filters;
    const { start_date, end_date } = parseTimespanFilter(filters as any);
    const { start_age, end_age } = parseAgeFilter(filters.start_age as any);
    const allFilters = { ...filters, practice_group_id: selectCurrentGroupId(state), start_date, end_date, start_age, end_age };
    try {
      return await fetchData({ endpoint: 'insights/microlearnings/most_claimed_microlearnings', filters: allFilters as any });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch most claimed microlearnings');
    }
  },
  { condition: (_, { getState }) => getState().microlearnings.mostClaimed.status !== 'loading' }
);

const microlearningsSlice = createSlice({
  name: 'microlearnings',
  initialState,
  reducers: {
    resetMicrolearningsData: () => initialState,
    setMicrolearningsFilters: (state, action: PayloadAction<Partial<MicrolearningsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHoursWatched.pending, (state) => { state.rollingMetrics.totalHoursWatched.status = 'loading'; state.rollingMetrics.totalHoursWatched.error = null; })
      .addCase(fetchHoursWatched.fulfilled, (state, action) => { state.rollingMetrics.totalHoursWatched.status = 'succeeded'; state.rollingMetrics.totalHoursWatched.data = action.payload; })
      .addCase(fetchHoursWatched.rejected, (state, action) => { state.rollingMetrics.totalHoursWatched.status = 'failed'; state.rollingMetrics.totalHoursWatched.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchTotalWatched.pending, (state) => { state.rollingMetrics.totalMicrolearningsWatched.status = 'loading'; state.rollingMetrics.totalMicrolearningsWatched.error = null; })
      .addCase(fetchTotalWatched.fulfilled, (state, action) => { state.rollingMetrics.totalMicrolearningsWatched.status = 'succeeded'; state.rollingMetrics.totalMicrolearningsWatched.data = action.payload; })
      .addCase(fetchTotalWatched.rejected, (state, action) => { state.rollingMetrics.totalMicrolearningsWatched.status = 'failed'; state.rollingMetrics.totalMicrolearningsWatched.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchCreditsClaimed.pending, (state) => { state.rollingMetrics.totalCreditsClaimed.status = 'loading'; state.rollingMetrics.totalCreditsClaimed.error = null; })
      .addCase(fetchCreditsClaimed.fulfilled, (state, action) => { state.rollingMetrics.totalCreditsClaimed.status = 'succeeded'; state.rollingMetrics.totalCreditsClaimed.data = action.payload; })
      .addCase(fetchCreditsClaimed.rejected, (state, action) => { state.rollingMetrics.totalCreditsClaimed.status = 'failed'; state.rollingMetrics.totalCreditsClaimed.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchPopularSeries.pending, (state) => { state.rollingMetrics.mostPopular.status = 'loading'; state.rollingMetrics.mostPopular.error = null; })
      .addCase(fetchPopularSeries.fulfilled, (state, action) => { state.rollingMetrics.mostPopular.status = 'succeeded'; state.rollingMetrics.mostPopular.data = action.payload; })
      .addCase(fetchPopularSeries.rejected, (state, action) => { state.rollingMetrics.mostPopular.status = 'failed'; state.rollingMetrics.mostPopular.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchMostWatched.pending, (state) => { state.mostWatched.status = 'loading'; state.mostWatched.error = null; })
      .addCase(fetchMostWatched.fulfilled, (state, action) => { state.mostWatched.status = 'succeeded'; state.mostWatched.data = action.payload; })
      .addCase(fetchMostWatched.rejected, (state, action) => { state.mostWatched.status = 'failed'; state.mostWatched.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchMostClaimed.pending, (state) => { state.mostClaimed.status = 'loading'; state.mostClaimed.error = null; })
      .addCase(fetchMostClaimed.fulfilled, (state, action) => { state.mostClaimed.status = 'succeeded'; state.mostClaimed.data = action.payload; })
      .addCase(fetchMostClaimed.rejected, (state, action) => { state.mostClaimed.status = 'failed'; state.mostClaimed.error = (action.payload as string) ?? action.error.message ?? null; });
  },
});

export const { resetMicrolearningsData, setMicrolearningsFilters } = microlearningsSlice.actions;
export default microlearningsSlice.reducer;